/**
 * Grille imbriquée — colonnes principales → lignes → colonnes (zones Puck).
 */

import {
  GRID_COLUMNS,
  createSectionColumnId,
  equalColumnSpans,
  getResolvedGridSpans,
  getSpanAt,
  normalizeSpanInput,
} from './proposalSectionColumns';

function createRow(columnCount = 1) {
  const columns = Array.from({ length: columnCount }, () => ({
    id: createSectionColumnId(),
  }));
  return {
    id: createSectionColumnId(),
    columnSpans: columnCount > 1 ? equalColumnSpans(columnCount) : [],
    columns,
  };
}

function createMainColumn(rowCount = 1, cellsPerRow = 1) {
  return {
    id: createSectionColumnId(),
    rows: Array.from({ length: rowCount }, () => createRow(cellsPerRow)),
  };
}

/** Ligne plate → nested 1 col principale × 1 ligne (conserve les ids zones Puck). */
export function flatRowToNested(columnIds, columnSpans = []) {
  const ids = [...columnIds];
  const n = ids.length;
  if (n === 0) return defaultNestedLayout();

  const spans = n > 1
    ? (Array.isArray(columnSpans) && columnSpans.length >= n - 1
      ? columnSpans.slice(0, n - 1)
      : equalColumnSpans(n))
    : [];

  return {
    mainColumnSpans: [],
    mainColumns: [{
      id: createSectionColumnId(),
      rows: [{
        id: createSectionColumnId(),
        columnSpans: spans,
        columns: ids.map((id) => ({ id })),
      }],
    }],
  };
}

/**
 * Ligne de N colonnes → nested équivalent : N colonnes principales × 1 ligne
 * × 1 colonne. Réutilise les ids de zones existants (mêmes ids ⇒ mêmes zones
 * Puck : le contenu reste dans ses colonnes — invariant non-destructif).
 * Les largeurs de colonnes deviennent les largeurs des colonnes principales.
 */
export function rowToNestedLayout(columnIds, columnSpans = []) {
  const ids = (columnIds || []).filter(Boolean);
  const n = ids.length;
  if (n === 0) return defaultNestedLayout();

  const mainColumnSpans = n > 1
    ? (Array.isArray(columnSpans) && columnSpans.length >= n - 1
      ? columnSpans.slice(0, n - 1)
      : equalColumnSpans(n))
    : [];

  return {
    mainColumnSpans,
    mainColumns: ids.map((id) => ({
      id: createSectionColumnId(),
      rows: [{
        id: createSectionColumnId(),
        columnSpans: [],
        columns: [{ id }],
      }],
    })),
  };
}

/** Layout leading item par défaut : grand bloc + pile latérale. */
export function defaultNestedLayout() {
  return {
    mainColumnSpans: [{ base: GRID_COLUMNS, md: 8 }],
    mainColumns: [
      createMainColumn(1, 1),
      createMainColumn(3, 1),
    ],
  };
}

/** Tous les ids feuilles (zones DropZone). */
export function collectLeafColumnIds(nested) {
  if (!nested?.mainColumns?.length) return [];
  const ids = [];
  nested.mainColumns.forEach((mainCol) => {
    (mainCol.rows || []).forEach((row) => {
      (row.columns || []).forEach((cell) => {
        if (cell?.id) ids.push(cell.id);
      });
    });
  });
  return ids;
}

export function getMainColumnSpanValues(nested) {
  const n = nested?.mainColumns?.length ?? 0;
  const ids = Array.from({ length: n }, (_, i) => `mc-${i}`);
  return getResolvedGridSpans(ids, nested?.mainColumnSpans || []);
}

export function getRowColumnSpanValues(row) {
  const n = row?.columns?.length ?? 0;
  const ids = Array.from({ length: n }, (_, i) => `rc-${i}`);
  return getResolvedGridSpans(ids, row?.columnSpans || []);
}

export function normalizeNested(nested) {
  if (!nested?.mainColumns?.length) return defaultNestedLayout();
  const mainColumns = nested.mainColumns.map((mc) => ({
    id: mc.id || createSectionColumnId(),
    rows: (mc.rows || [createRow(1)]).map((row) => {
      const columns = (row.columns || []).map((c) => ({
        id: c.id || createSectionColumnId(),
      }));
      const columnSpans = Array.isArray(row.columnSpans)
        ? row.columnSpans.slice(0, Math.max(0, columns.length - 1))
        : equalColumnSpans(columns.length);
      return {
        id: row.id || createSectionColumnId(),
        columnSpans: columns.length > 1 ? columnSpans : [],
        columns,
      };
    }),
  }));

  const expectedMainSpans = Math.max(0, mainColumns.length - 1);
  let mainColumnSpans = Array.isArray(nested.mainColumnSpans)
    ? nested.mainColumnSpans.slice(0, expectedMainSpans)
    : [];
  if (mainColumnSpans.length < expectedMainSpans) {
    mainColumnSpans = equalColumnSpans(mainColumns.length);
  }

  return { mainColumnSpans, mainColumns };
}

export function applyNestedMainColumnCount(nested, count) {
  const n = Math.max(1, Math.min(4, count));
  const current = normalizeNested(nested);
  const next = { ...current, mainColumns: [...current.mainColumns] };

  while (next.mainColumns.length < n) {
    next.mainColumns.push(createMainColumn(1, 1));
  }
  while (next.mainColumns.length > n) {
    next.mainColumns.pop();
  }
  next.mainColumnSpans = n > 1 ? equalColumnSpans(n) : [];

  return next;
}

export function setNestedMainColumnSpan(nested, index, spanMd) {
  const current = normalizeNested(nested);
  const n = current.mainColumns.length;
  if (n <= 1 || index < 0 || index >= n - 1) return current;

  const nextSpans = [...current.mainColumnSpans];
  const others = nextSpans.reduce((sum, s, i) => (i === index ? sum : sum + getSpanAt(s, 'md')), 0);
  const max = GRID_COLUMNS - 1 - others;
  const md = Math.max(1, Math.min(max, Math.round(Number(spanMd)) || 1));
  nextSpans[index] = { base: GRID_COLUMNS, md };

  return { ...current, mainColumnSpans: nextSpans };
}

export function setNestedRowCount(nested, mainIndex, rowCount) {
  const current = normalizeNested(nested);
  const n = Math.max(1, Math.min(8, rowCount));
  const next = {
    ...current,
    mainColumns: current.mainColumns.map((mc, i) => {
      if (i !== mainIndex) return mc;
      const rows = [...(mc.rows || [])];
      while (rows.length < n) rows.push(createRow(1));
      while (rows.length > n) rows.pop();
      return { ...mc, rows };
    }),
  };
  return next;
}

export function setNestedRowColumnCount(nested, mainIndex, rowIndex, columnCount) {
  const current = normalizeNested(nested);
  const n = Math.max(1, Math.min(6, columnCount));
  const next = {
    ...current,
    mainColumns: current.mainColumns.map((mc, mi) => {
      if (mi !== mainIndex) return mc;
      return {
        ...mc,
        rows: (mc.rows || []).map((row, ri) => {
          if (ri !== rowIndex) return row;
          const columns = [...(row.columns || [])];
          while (columns.length < n) {
            columns.push({ id: createSectionColumnId() });
          }
          while (columns.length > n) {
            columns.pop();
          }
          return {
            ...row,
            columns,
            columnSpans: n > 1 ? equalColumnSpans(n) : [],
          };
        }),
      };
    }),
  };
  return next;
}

export function setNestedRowColumnSpan(nested, mainIndex, rowIndex, colIndex, spanMd) {
  const current = normalizeNested(nested);
  const next = {
    ...current,
    mainColumns: current.mainColumns.map((mc, mi) => {
      if (mi !== mainIndex) return mc;
      return {
        ...mc,
        rows: (mc.rows || []).map((row, ri) => {
          if (ri !== rowIndex) return row;
          const colN = row.columns.length;
          if (colN <= 1 || colIndex < 0 || colIndex >= colN - 1) return row;
          const nextSpans = [...(row.columnSpans || [])];
          const others = nextSpans.reduce((sum, s, i) => (
            i === colIndex ? sum : sum + getSpanAt(s, 'md')
          ), 0);
          const max = GRID_COLUMNS - 1 - others;
          const md = Math.max(1, Math.min(max, Math.round(Number(spanMd)) || 1));
          nextSpans[colIndex] = { base: GRID_COLUMNS, md };
          return { ...row, columnSpans: nextSpans };
        }),
      };
    }),
  };
  return next;
}

/** Ancien preset vertical (N colonnes plates) → nested 1 ligne × N colonnes. */
export function migrateVerticalToNested(verticalColumnCount = 3) {
  const n = Math.max(2, Math.min(6, verticalColumnCount));
  return {
    mainColumnSpans: [],
    mainColumns: [
      {
        id: createSectionColumnId(),
        rows: [
          {
            id: createSectionColumnId(),
            columnSpans: n > 1 ? equalColumnSpans(n) : [],
            columns: Array.from({ length: n }, () => ({ id: createSectionColumnId() })),
          },
        ],
      },
    ],
  };
}

export function spanPropForGrid(span) {
  if (span == null) return GRID_COLUMNS;
  if (typeof span === 'number') return span;
  return normalizeSpanInput(span);
}
